// Reusable wedding-themed SVG decoration primitives
import { motion } from "framer-motion";

// Glowing oil lamp (diya)
export function Diya({ size = 36, className = "", delay = 0 }) {
  return (
    <motion.div
      aria-hidden="true"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size * 1.4 }}
    >
      {/* Flame glow */}
      <motion.div
        aria-hidden="true"
        animate={{ opacity: [0.6, 1, 0.6], scale: [0.95, 1.08, 0.95] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-1/2 -translate-x-1/2 rounded-full"
        style={{
          top: -size * 0.1,
          width: size * 0.9,
          height: size * 0.9,
          background:
            "radial-gradient(circle, rgba(255,210,130,0.9) 0%, rgba(232,165,180,0.35) 40%, rgba(232,165,180,0) 70%)",
          filter: "blur(6px)",
        }}
      />
      <svg viewBox="0 0 50 70" width={size} height={size * 1.4} className="relative">
        <defs>
          <linearGradient id="diyaBowl" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#8B4513" />
            <stop offset="50%" stopColor="#D4AF37" />
            <stop offset="100%" stopColor="#5C2E0E" />
          </linearGradient>
          <radialGradient id="flame" cx="0.5" cy="0.7" r="0.6">
            <stop offset="0%" stopColor="#FFF6D0" />
            <stop offset="45%" stopColor="#F2C94C" />
            <stop offset="100%" stopColor="#D4AF37" />
          </radialGradient>
        </defs>
        {/* Bowl */}
        <path
          d="M 5 40 Q 25 60 45 40 L 42 50 Q 25 60 8 50 Z"
          fill="url(#diyaBowl)"
          stroke="#D4AF37"
          strokeWidth="0.8"
        />
        {/* Wick base */}
        <ellipse cx="25" cy="38" rx="4" ry="1.5" fill="#1a0c04" />
        {/* Flame flicker */}
        <motion.path
          d="M 25 10 Q 20 25 25 35 Q 30 25 25 10 Z"
          fill="url(#flame)"
          animate={{
            d: [
              "M 25 10 Q 20 25 25 35 Q 30 25 25 10 Z",
              "M 25 8 Q 18 24 25 36 Q 32 23 25 8 Z",
              "M 25 10 Q 20 25 25 35 Q 30 25 25 10 Z",
            ],
          }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
          style={{ filter: "drop-shadow(0 0 6px rgba(242,201,76,0.8))" }}
        />
      </svg>
    </motion.div>
  );
}

// Rangoli-inspired corner ornament (mandala quadrant)
export function RangoliCorner({ size = 120, rotate = 0, className = "", color = "gold" }) {
  const stroke = color === "rose" ? "var(--iw-rose)" : "var(--iw-gold)";
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={{ transform: `rotate(${rotate}deg)`, color: stroke }}
    >
      <g stroke="currentColor" strokeWidth="0.7" fill="none" opacity="0.6">
        {/* Petals arc */}
        <path d="M 0 50 A 50 50 0 0 1 50 0" />
        <path d="M 10 50 A 40 40 0 0 1 50 10" />
        <path d="M 0 50 Q 15 40 25 25 Q 40 15 50 0" strokeDasharray="2 2" />
        {/* Flower petals */}
        {Array.from({ length: 5 }).map((_, i) => {
          const a = (i * 18 - 5) * (Math.PI / 180);
          const r = 35;
          const cx = Math.cos(a) * r;
          const cy = 50 - Math.sin(a) * r;
          return (
            <g key={i} transform={`translate(${cx},${cy})`}>
              <ellipse cx="0" cy="0" rx="3" ry="6" />
              <circle cx="0" cy="0" r="1" fill="currentColor" opacity="0.8" />
            </g>
          );
        })}
        {/* Dots */}
        <circle cx="0" cy="50" r="1.5" fill="currentColor" />
        <circle cx="50" cy="0" r="1.5" fill="currentColor" />
        <circle cx="5" cy="30" r="0.8" fill="currentColor" />
        <circle cx="30" cy="5" r="0.8" fill="currentColor" />
      </g>
    </svg>
  );
}

// Mehendi-style ornamental border strip (SVG stroke-drawn paisleys)
export function MehendiBorder({ className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-3 ${className}`}>
      <span className="h-px w-10 md:w-20 bg-gradient-to-r from-transparent via-[var(--iw-gold)]/50 to-[var(--iw-rose)]/40" />
      <motion.svg
        aria-hidden="true"
        viewBox="0 0 400 40"
        width="400"
        height="40"
        className="shrink-0"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-60px" }}
        style={{ color: "var(--iw-gold)" }}
      >
        <motion.g
          stroke="currentColor"
          strokeWidth="0.8"
          fill="none"
          strokeLinecap="round"
          variants={{
            hidden: { pathLength: 0, opacity: 0 },
            visible: {
              pathLength: 1,
              opacity: 1,
              transition: { duration: 2.2, ease: [0.16, 1, 0.3, 1] },
            },
          }}
        >
          {/* Left paisley */}
          <motion.path d="M 20 20 C 20 8, 60 8, 60 20 C 60 32, 20 32, 20 20 Z" />
          <motion.path d="M 10 20 Q 30 4, 55 12" />
          <motion.path d="M 10 20 Q 30 36, 55 28" />
          <motion.circle cx="38" cy="20" r="1.3" fill="currentColor" />
          {/* Vine center */}
          <motion.path d="M 70 20 Q 100 8 130 20 T 190 20 T 250 20 T 310 20 T 380 20" />
          {/* Leaves along vine */}
          {[90, 120, 150, 180, 210, 240, 270, 300, 330, 360].map((x, i) => (
            <motion.path
              key={i}
              d={`M ${x} 20 Q ${x + 4} ${i % 2 === 0 ? 10 : 30} ${x + 10} 20`}
              fill={i % 2 === 0 ? "currentColor" : "none"}
              opacity={i % 2 === 0 ? 0.6 : 1}
            />
          ))}
          {/* Right mirror paisley */}
          <motion.circle cx="380" cy="20" r="1.3" fill="currentColor" />
        </motion.g>
      </motion.svg>
      <span className="h-px w-10 md:w-20 bg-gradient-to-l from-transparent via-[var(--iw-gold)]/50 to-[var(--iw-rose)]/40" />
    </div>
  );
}

// Mandap torana — decorative marigold/floral garland
export function Torana({ className = "" }) {
  const flowers = Array.from({ length: 18 }, (_, i) => ({
    x: 20 + i * 50,
    y: 30 + Math.sin(i * 0.35) * 20,
    size: 6 + (i % 3) * 2,
  }));
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 960 80"
      preserveAspectRatio="none"
      className={`w-full h-16 md:h-20 ${className}`}
    >
      {/* Garland string */}
      <path
        d="M 0 40 Q 240 80 480 40 T 960 40"
        stroke="rgba(212,175,55,0.55)"
        strokeWidth="1"
        fill="none"
      />
      {/* Flower buds along garland */}
      {flowers.map((f, i) => (
        <g key={i} transform={`translate(${f.x} ${f.y + Math.sin(i * 0.9) * 12})`}>
          <motion.circle
            r={f.size}
            fill={i % 3 === 0 ? "var(--iw-rose)" : "var(--iw-gold)"}
            opacity={0.85}
            animate={{ scale: [0.9, 1.08, 0.9] }}
            transition={{
              duration: 3 + (i % 3),
              repeat: Infinity,
              delay: i * 0.12,
              ease: "easeInOut",
            }}
            style={{ filter: "drop-shadow(0 0 4px rgba(212,175,55,0.5))" }}
          />
          <circle r={f.size * 0.4} fill="#6A1B2A" opacity="0.5" />
        </g>
      ))}
      {/* Hanging tassels */}
      {[80, 240, 480, 720, 880].map((x, i) => (
        <g key={i}>
          <line
            x1={x}
            y1={40 + Math.sin(x * 0.01) * 8}
            x2={x}
            y2={80}
            stroke="var(--iw-gold)"
            strokeWidth="0.8"
            opacity="0.6"
          />
          <circle cx={x} cy="76" r="2.5" fill="var(--iw-rose)" />
        </g>
      ))}
    </svg>
  );
}
